import { useState, useEffect, useCallback } from "react";
import { 
  getUserJourney,
  UserJourney,
} from "../services/menuClick_service";
import { toast } from "react-toastify";
import { 
  FaSearch, 
  FaTimes, 
  FaCalendarAlt, 
  FaUsers, 
  FaUndo,
  FaUserTag,
  FaUserCheck,
  FaCircle,
  FaUserSecret,
  FaChartBar,
  FaTrash} from "react-icons/fa";
import UserJourneyModal from './UserJourneyModal';
import { 
  getClientsWithOnlineStatusPaginated,
  deleteAnonymousUsers,
} from "../services/Registre_service";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import DeleteAnonymousModal from "./DeleteAnonymousModal";
import LoadingSpinner from "./LoadingSpinner";
import ClientsTable from "./ClientsTable";
import ClientsPagination from "./ClientsPagination";
import ClientConnectionStatsPanel from "./ClientConnectionStatsPanel";

import { useLiveStats } from "../hooks/useLiveStats";

// Interfaces
 export interface ClientWithStatus {
  id: number;
  email: string;
  fullName: string;
  firstName: string;
  lastName: string;
  userType: 'personne' | 'entreprise' | 'professionnel';
  accountStatus: 'active' | 'inactive' | 'waiting_validation' | 'anonymous';
  connectionStatus: 'online' | 'idle' | 'offline';
  isOnline: boolean;
  isLive: boolean;
  timestamps: {
    lastActivity: string | null;
    lastLogin: string | null;
    lastLogout: string | null;
    lastActivityAgo: string | null;
    lastLoginAgo: string | null;
  };
  profession: string | null;
  profilePicture: string | null;
  createdAt: string;
}

// Types pour les filtres
type ConnectionFilter = 'all' | 'online' | 'idle' | 'offline';

const drawerStyles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
    transition: 'opacity 0.3s ease-in-out',
  },
  drawer: {
    position: 'fixed' as const,
    top: 0,
    right: 0,
    width: '700px',
    maxWidth: '90vw',
    height: '100vh',
    backgroundColor: 'white',
    boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
    transform: 'translateX(0)',
    transition: 'transform 0.3s ease-in-out',
    zIndex: 1001,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column' as const,
  },
};

// Constantes pour les filtres
const PERIODS = [
  { label: "Aujourd'hui", value: "today" },
  { label: "7 derniers jours", value: "7d" },
  { label: "30 derniers jours", value: "30d" },
  { label: "90 derniers jours", value: "3m" },
  { label: "Cette année", value: "1y" },
  { label: "Personnalisé", value: "custom" },
];

const USER_TYPES = [
  { label: "Tous", value: "" },
  { label: "Personne", value: "personne" },
  { label: "Entreprise", value: "entreprise" },
  { label: "Professionnel", value: "professionnel" },
];

// ✅ "Anonyme" retiré — les anonymes ont leur propre onglet dédié
const ACCOUNT_STATUSES = [
  { label: "Tous", value: "" },
  { label: "Actif", value: "active" },
  { label: "En attente", value: "waiting_validation" },
  { label: "Inactif", value: "inactive" },
];

const CONNECTION_FILTERS = [
  { label: "Tous", value: "all" },
  { label: "Connectés", value: "online", color: "#10B981" },
  { label: "Inactifs", value: "idle", color: "#D97706" },
  { label: "Déconnectés", value: "offline", color: "#B91C1C" },
];

// Helpers
const formatNumber = (num: number): string => 
  new Intl.NumberFormat('fr-FR').format(num);

export default function ClientsOnlineDashboard() {
  // State pour les onglets
  const [activeTab, setActiveTab] = useState<'all' | 'anonymous' | 'stats'>('all');
  const isStatsTab = activeTab === 'stats';
  
  // State pour la recherche
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  
  // State pour les filtres
  const [selectedPeriod, setSelectedPeriod] = useState("30d");
  const [customFromDate, setCustomFromDate] = useState("");
  const [customToDate, setCustomToDate] = useState("");
  const [selectedUserType, setSelectedUserType] = useState("");
  const [selectedAccountStatus, setSelectedAccountStatus] = useState("");
  const [selectedConnectionFilter, setSelectedConnectionFilter] = useState<ConnectionFilter>('all');
  
  const [debouncedConnectionFilter, setDebouncedConnectionFilter] = useState<ConnectionFilter>('all');
  
  // Données clients
  const [clients, setClients] = useState<ClientWithStatus[]>([]);
  const [anonymousClients, setAnonymousClients] = useState<ClientWithStatus[]>([]);
  const [clientsPagination, setClientsPagination] = useState({ 
    page: 1, 
    limit: 100, 
    total: 0, 
    totalPages: 1 
  });
  const [anonymousPagination, setAnonymousPagination] = useState({ 
    page: 1, 
    limit: 100, 
    total: 0, 
    totalPages: 1 
  });
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingAnonymous, setLoadingAnonymous] = useState(false);

  // ✅ States locaux — alimentés par le socket (voir ci-dessous)
  const [liveCount, setLiveCount] = useState(0);
  const [offlineCount, setOfflineCount] = useState(0);

  // Modale
  const [selectedUser, setSelectedUser] = useState<UserJourney | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);

  // États pour la suppression
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<{
    ids: number[];
    emails: string[];
    deleteAll: boolean;
  }>({ ids: [], emails: [], deleteAll: false });
  const [deleting, setDeleting] = useState(false);

  // État pour les cases à cocher (uniquement pour les anonymes)
  const [selectedAnonymousIds, setSelectedAnonymousIds] = useState<number[]>([]);

  const {
    liveCount: socketLiveCount,
    offlineCount: socketOfflineCount,
  } = useLiveStats({
    initialLive: liveCount,
    // ✅ Le total des métriques ne concerne que les clients enregistrés (non anonymes)
    initialTotal: clientsPagination.total,
  });

  useEffect(() => {
    setLiveCount(socketLiveCount);
    setOfflineCount(socketOfflineCount);
  }, [socketLiveCount, socketOfflineCount]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedConnectionFilter(selectedConnectionFilter);
    }, 300);
    return () => clearTimeout(timer);
  }, [selectedConnectionFilter]);

  useEffect(() => {
    if (activeTab === 'stats') {
      setSelectedPeriod('today');
      setCustomFromDate('');
      setCustomToDate('');
      setSelectedUserType('');
      setSelectedAccountStatus('');
      setSelectedConnectionFilter('all');
    }
  }, [activeTab]);

  const fetchClientsWithStatus = useCallback(async () => {
    setLoadingClients(true);
    try {
      const params: any = {
        page: clientsPagination.page,
        limit: clientsPagination.limit,
        search: debouncedSearchTerm || undefined,
        userType: selectedUserType || undefined,
        // ✅ Si aucun filtre de statut explicite, on exclut systématiquement les anonymes
        status: selectedAccountStatus || undefined,
        excludeAnonymous: true,
      };

      if (selectedPeriod === 'custom' && customFromDate && customToDate) {
        params.from = customFromDate;
        params.to = customToDate;
      } else if (selectedPeriod && selectedPeriod !== 'custom') {
        params.period = selectedPeriod;
      }

      if (debouncedConnectionFilter !== 'all') {
        params.connectionStatus = debouncedConnectionFilter;
      }

      console.debug('Fetching clients with params:', params);

      const response = await getClientsWithOnlineStatusPaginated(params);
      
      if (response.success) {
        setClients(response.data);
        setClientsPagination({
          page: response.pagination.page,
          limit: response.pagination.limit,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages
        });
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des clients");
      console.error(error);
    } finally {
      setLoadingClients(false);
    }
  }, [
    clientsPagination.page, 
    clientsPagination.limit, 
    debouncedSearchTerm, 
    selectedUserType, 
    selectedAccountStatus, 
    debouncedConnectionFilter, 
    selectedPeriod,
    customFromDate,
    customToDate
  ]);

  const fetchAnonymousClients = useCallback(async () => {
    setLoadingAnonymous(true);
    try {
      const params: any = {
        page: anonymousPagination.page,
        limit: anonymousPagination.limit,
        search: debouncedSearchTerm || undefined,
      };

      if (selectedPeriod === 'custom' && customFromDate && customToDate) {
        params.from = customFromDate;
        params.to = customToDate;
      } else if (selectedPeriod && selectedPeriod !== 'custom') {
        params.period = selectedPeriod;
      }

      params.status = 'anonymous';
      
      if (debouncedConnectionFilter !== 'all') {
        params.connectionStatus = debouncedConnectionFilter;
      }

      console.debug('Fetching anonymous clients with params:', params);

      const response = await getClientsWithOnlineStatusPaginated(params);
      
      if (response.success) {
        setAnonymousClients(response.data);
        setAnonymousPagination({
          page: response.pagination.page,
          limit: response.pagination.limit,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages
        });
        setSelectedAnonymousIds([]);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement des clients anonymes");
      console.error(error);
    } finally {
      setLoadingAnonymous(false);
    }
  }, [anonymousPagination.page, anonymousPagination.limit, debouncedSearchTerm, debouncedConnectionFilter, selectedPeriod, customFromDate, customToDate]);

  // Charger les clients selon l'onglet actif
  useEffect(() => {
    if (activeTab === 'all') {
      fetchClientsWithStatus();
    } else if (activeTab === 'anonymous') {
      fetchAnonymousClients();
    }
  }, [
    activeTab, 
    clientsPagination.page, 
    anonymousPagination.page, 
    debouncedSearchTerm,
    selectedUserType,
    selectedAccountStatus,
    debouncedConnectionFilter,
    selectedPeriod,
    customFromDate,
    customToDate,
    fetchClientsWithStatus, 
    fetchAnonymousClients
  ]);

  // Reset à la page 1 quand les filtres changent
  useEffect(() => {
    setClientsPagination(prev => ({ ...prev, page: 1 }));
    setAnonymousPagination(prev => ({ ...prev, page: 1 }));
  }, [
    debouncedSearchTerm,
    selectedUserType,
    selectedAccountStatus,
    debouncedConnectionFilter,
    selectedPeriod,
    customFromDate,
    customToDate
  ]);

  useEffect(() => {
  const fetchAnonymousCount = async () => {
    try {
      const response = await getClientsWithOnlineStatusPaginated({
        page: 1,
        limit: 1, // on veut juste le total, pas les données
        status: 'anonymous',
      });
      if (response.success) {
        setAnonymousPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages,
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  fetchAnonymousCount();
}, []);

  const openUserDetails = async (userId: number) => {
    setModalLoading(true);
    setModalIsOpen(true);
    try {
      const result = await getUserJourney(userId, {});

      console.log("Resultat de getUserJourney:", result);
      
      if (result && 'noHistory' in result) {
        const emptyJourney: UserJourney = {
          user: { 
            id: userId, 
            name: '',
            firstname: '',
            email: '',
            user_type: 'personne',
            status: 'active',
            fullName: `Utilisateur #${userId}`,
            profile_picture: ''
          },
          stats: {
            total_clicks: 0,
            first_click: '',
            last_click: '',
            total_sessions: 0,
            distinct_menus: 0
          },
          journey: [], 
          sessions: [] 
        };
        
        setSelectedUser(emptyJourney);
        
        toast.info("Cet utilisateur n'a pas encore d'historique d'activité", {
          position: "top-center",
          autoClose: 3000,
          hideProgressBar: true
        });
        
      } else if (result) {
        setSelectedUser(result as UserJourney);
      } else {
        setModalIsOpen(false);
      }
    } catch (error) {
      console.error("Erreur:", error);
      setModalIsOpen(false);
    } finally {
      setModalLoading(false);
    }
  };
  
  const clearSearch = () => {
    setSearchTerm("");
  };

  const changePage = (newPage: number) => {
    if (activeTab === 'all') {
      setClientsPagination(prev => ({ ...prev, page: newPage }));
    } else {
      setAnonymousPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setSelectedPeriod("30d");
    setCustomFromDate("");
    setCustomToDate("");
    setSelectedUserType("");
    setSelectedAccountStatus("");
    setSelectedConnectionFilter('all');
  };

  // Fonctions pour la gestion des cases à cocher
  const toggleSelectAll = () => {
    if (selectedAnonymousIds.length === anonymousClients.length) {
      setSelectedAnonymousIds([]);
    } else {
      setSelectedAnonymousIds(anonymousClients.map(c => c.id));
    }
  };

  const toggleSelectClient = (clientId: number) => {
    setSelectedAnonymousIds(prev => 
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleDeleteSelected = () => {
    if (selectedAnonymousIds.length === 0) {
      toast.warning("Veuillez sélectionner au moins un utilisateur");
      return;
    }
    const selectedClients = anonymousClients.filter(c => selectedAnonymousIds.includes(c.id));
    setSelectedForDelete({
      ids: selectedAnonymousIds,
      emails: selectedClients.map(c => c.email),
      deleteAll: false
    });
    setDeleteModalOpen(true);
  };

  const handleDeleteAll = () => {
    setSelectedForDelete({ ids: [], emails: [], deleteAll: true });
    setDeleteModalOpen(true);
  };

  const handleDeleteSingle = (client: ClientWithStatus) => {
    setSelectedForDelete({ ids: [client.id], emails: [client.email], deleteAll: false });
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      let result;
      if (selectedForDelete.deleteAll) {
        result = await deleteAnonymousUsers(undefined, true);
        toast.success(`${result.deletedCount} utilisateur(s) anonyme(s) supprimé(s)`);
      } else {
        result = await deleteAnonymousUsers(selectedForDelete.ids);
        toast.success(`${result.deletedCount} utilisateur(s) supprimé(s)`);
      }
      await fetchAnonymousClients();
      setSelectedAnonymousIds([]);
      setDeleteModalOpen(false);
    } catch (error: any) {
      toast.error(error.message || "Erreur lors de la suppression");
    } finally {
      setDeleting(false);
    }
  };

  return (
    
    <div style={{ 
      padding: 32, 
      background: "#F3F4F6", 
      minHeight: "100vh",
      fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      position: "relative"
    }}>

      <ToastContainer 
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        limit={3}
        containerId="global"
        style={{ zIndex: 9999 }} 
      />
      {/* Header */}
      <div style={{ 
        marginBottom: 28,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            background: "#4bb0c0",
            width: 48,
            height: 48,
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontSize: 24
          }}>
            <FaUsers />
          </div>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 600, color: "#111827", margin: 0 }}>
              Clients
            </h1>
            <p style={{ color: "#6B7280", marginTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
              <FaCircle style={{ fontSize: 12, color: liveCount > 0 ? '#10B981' : '#9CA3AF' }} />
              {liveCount} client{liveCount > 1 ? 's' : ''} actif{liveCount > 1 ? 's' : ''} en ce moment
            </p>
          </div>
        </div>
        
        {/* ✅ Cartes statistiques — reflètent uniquement les clients enregistrés (non anonymes) */}
        <div style={{ display: 'flex', gap: 16 }}>
          {/* Carte connectés */}
          <div style={{
            background: 'white',
            borderRadius: 12,
            padding: '16px 24px',
            border: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
          }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: '#D1FAE5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#059669',
              fontSize: 24
            }}>
              <FaCircle />
            </div>
            <div>
              <div style={{ color: '#6B7280', fontSize: 14 }}>Connectés</div>
              <div style={{ fontSize: 28, fontWeight: 600, color: '#111827' }}>{liveCount}</div>
            </div>
          </div>

          {/* Carte déconnectés */}
          <div style={{
            background: 'white',
            borderRadius: 12,
            padding: '16px 24px',
            border: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
          }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: '#FEE2E2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#B91C1C',
              fontSize: 24
            }}>
              <FaCircle />
            </div>
            <div>
              <div style={{ color: '#6B7280', fontSize: 14 }}>Déconnectés</div>
              <div style={{ fontSize: 28, fontWeight: 600, color: '#111827' }}>{offlineCount}</div>
            </div>
          </div>

          {/* Carte total — uniquement les clients enregistrés */}
          <div style={{
            background: 'white',
            borderRadius: 12,
            padding: '16px 24px',
            border: '1px solid #E5E7EB',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
          }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: '#EFF6FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#2563EB',
              fontSize: 24
            }}>
              <FaUsers />
            </div>
            <div>
              <div style={{ color: '#6B7280', fontSize: 14 }}>Total clients</div>
              <div style={{ fontSize: 28, fontWeight: 600, color: '#111827' }}>
                {formatNumber(clientsPagination.total)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div style={{
        background: "white",
        borderRadius: 16,
        padding: 24,
        marginBottom: 28,
        border: "1px solid #E5E7EB",
        boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
      }}>
        {/* Première ligne - Recherche et filtres principaux */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isStatsTab ? '1fr' : '2fr 1fr 1fr 1fr',
          gap: 16,
          marginBottom: 20
        }}>
          {!isStatsTab && (
            <div>
              <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 4 }}>
                <FaSearch size={12} /> Recherche
              </label>
              <div style={{ position: "relative" }}>
                <FaSearch style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#9CA3AF",
                  fontSize: 14,
                  zIndex: 1
                }} />
                <input
                  type="text"
                  placeholder="Nom, prénom, email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    ...inputStyle,
                    width: "100%",
                    paddingLeft: 40,
                    paddingRight: searchTerm ? 40 : 12,
                    height: 42
                  }}
                />
                {searchTerm && (
                  <FaTimes
                    onClick={clearSearch}
                    style={{
                      position: "absolute",
                      right: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#9CA3AF",
                      fontSize: 16,
                      cursor: "pointer",
                      zIndex: 1
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* ✅ Filtre type utilisateur — masqué sur l'onglet anonymes */}
          {!isStatsTab && activeTab !== 'anonymous' && (
            <div>
              <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 4 }}>
                <FaUserTag size={12} /> Type d'utilisateur
              </label>
              <select
                value={selectedUserType}
                onChange={(e) => setSelectedUserType(e.target.value)}
                style={{ ...inputStyle, width: "100%", height: 42 }}
              >
                {USER_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* ✅ Filtre statut compte — masqué sur l'onglet anonymes (tous sont anonymes) */}
          {!isStatsTab && activeTab !== 'anonymous' && (
            <div>
              <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 4 }}>
                <FaUserCheck size={12} /> Statut compte
              </label>
              <select
                value={selectedAccountStatus}
                onChange={(e) => setSelectedAccountStatus(e.target.value)}
                style={{ ...inputStyle, width: "100%", height: 42 }}
              >
                {ACCOUNT_STATUSES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          )}

          {!isStatsTab && (
            <div>
              <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 4 }}>
                <FaCircle size={12} /> Statut connexion
              </label>
              <select
                value={selectedConnectionFilter}
                onChange={(e) => setSelectedConnectionFilter(e.target.value as ConnectionFilter)}
                style={{ ...inputStyle, width: "100%", height: 42 }}
              >
                {CONNECTION_FILTERS.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Deuxième ligne - Période et actions */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isStatsTab ? '2fr 1fr' : '2fr 1fr 1fr',
          gap: 16,
          alignItems: 'flex-end'
        }}>
          {/* Filtre période */}
          <div>
            <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: 4 }}>
              <FaCalendarAlt size={12} /> Période
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                style={{ ...inputStyle, width: '200px', height: 42 }}
              >
                {PERIODS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              
              {selectedPeriod === 'custom' && (
                <>
                  <input
                    type="date"
                    value={customFromDate}
                    onChange={(e) => setCustomFromDate(e.target.value)}
                    style={{ ...inputStyle, height: 42, flex: 1 }}
                    placeholder="Date début"
                  />
                  <input
                    type="date"
                    value={customToDate}
                    onChange={(e) => setCustomToDate(e.target.value)}
                    style={{ ...inputStyle, height: 42, flex: 1 }}
                    placeholder="Date fin"
                  />
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={resetFilters}
              style={secondaryButton}
            >
              <FaUndo style={{ marginRight: 6 }} />
              Réinitialiser
            </button>
          </div>

          {/* Boutons de suppression pour anonymes */}
          {activeTab === 'anonymous' && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              {selectedAnonymousIds.length > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  style={{
                    ...secondaryButton,
                    background: '#FEE2E2',
                    borderColor: '#B91C1C',
                    color: '#B91C1C'
                  }}
                >
                  <FaTrash style={{ marginRight: 6 }} />
                  Supprimer ({selectedAnonymousIds.length})
                </button>
              )}
              
              {anonymousClients.length > 0 && (
                <button
                  onClick={handleDeleteAll}
                  style={{
                    ...secondaryButton,
                    background: '#FEE2E2',
                    borderColor: '#B91C1C',
                    color: '#B91C1C'
                  }}
                >
                  <FaTrash style={{ marginRight: 6 }} />
                  Tout supprimer
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Onglets */}
      <div style={{
        display: 'flex',
        gap: 16,
        marginBottom: 20,
        borderBottom: '2px solid #E5E7EB',
        paddingBottom: 12
      }}>
        <button
          onClick={() => setActiveTab('all')}
          style={{
            ...tabStyle,
            borderBottom: activeTab === 'all' ? '3px solid #4bb0c0' : 'none',
            color: activeTab === 'all' ? '#4bb0c0' : '#6B7280'
          }}
        >
          <FaUsers style={{ marginRight: 8 }} />
          Tous les clients ({formatNumber(clientsPagination.total)})
        </button>
        <button
          onClick={() => setActiveTab('anonymous')}
          style={{
            ...tabStyle,
            borderBottom: activeTab === 'anonymous' ? '3px solid #4bb0c0' : 'none',
            color: activeTab === 'anonymous' ? '#4bb0c0' : '#6B7280'
          }}
        >
          <FaUserSecret style={{ marginRight: 8 }} />
          Utilisateurs anonymes ({formatNumber(anonymousPagination.total)})
        </button>
        <button
        onClick={() => setActiveTab('stats')}
        style={{
        ...tabStyle,
            borderBottom: activeTab === 'stats' ? '3px solid #4bb0c0' : 'none',
            color: activeTab === 'stats' ? '#4bb0c0' : '#6B7280'
          }}
        >
          <FaChartBar style={{ marginRight: 8 }} />
          Statistiques
        </button> 
      </div>

      {/* Modale de détails */}
      {modalIsOpen && (
        <>
          <div 
            style={{
              ...drawerStyles.overlay,
              opacity: modalIsOpen ? 1 : 0,
              pointerEvents: modalIsOpen ? 'auto' : 'none',
            }}
            onClick={() => setModalIsOpen(false)}
          />
          <div 
            style={{
              ...drawerStyles.drawer,
              transform: modalIsOpen ? 'translateX(0)' : 'translateX(100%)',
            }}
          >
            {modalLoading ? (
              <div style={{ padding: 80, textAlign: 'center' }}>
                <LoadingSpinner />
              </div>
            ) : selectedUser ? (
              <UserJourneyModal 
                user={selectedUser} 
                onClose={() => setModalIsOpen(false)} 
              />
            ) : null}
          </div>
        </>
      )}

      {/* Tableau des clients / stats selon l'onglet */}
      {activeTab === 'stats' ? (
        <ClientConnectionStatsPanel
          selectedPeriod={selectedPeriod}
          customFromDate={customFromDate}
          customToDate={customToDate}
          selectedUserType={selectedUserType}
          selectedAccountStatus={selectedAccountStatus}
        />
      ) : activeTab === 'all' ? (
        loadingClients ? (
          <LoadingSpinner /> 
        ) : (
          <div style={{ 
            background: "white", 
            borderRadius: 16, 
            border: "1px solid #E5E7EB", 
            padding: 24,
            boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
          }}>
            <ClientsTable 
              clients={clients} 
              onUserClick={openUserDetails}
              showCheckboxes={false}
              selectedIds={[]}
              onToggleSelect={() => {}}
              onToggleSelectAll={() => {}}
            />
            <ClientsPagination 
              pagination={{
                page: clientsPagination.page,
                totalPages: clientsPagination.totalPages,
                hasNextPage: clientsPagination.page < clientsPagination.totalPages,
                hasPrevPage: clientsPagination.page > 1
              }}
              onPageChange={changePage}
            />
          </div>
        )
      ) : (
        loadingAnonymous ? (
          <LoadingSpinner /> 
        ) : (
          <div style={{ 
            background: "white", 
            borderRadius: 16, 
            border: "1px solid #E5E7EB", 
            padding: 24,
            boxShadow: "0 2px 4px rgba(0,0,0,0.02)"
          }}>
            <ClientsTable 
              clients={anonymousClients} 
              onUserClick={openUserDetails}
              onDeleteClick={handleDeleteSingle}
              showCheckboxes={true}
              selectedIds={selectedAnonymousIds}
              onToggleSelect={toggleSelectClient}
              onToggleSelectAll={toggleSelectAll}
            />
            <ClientsPagination 
              pagination={{
                page: anonymousPagination.page,
                totalPages: anonymousPagination.totalPages,
                hasNextPage: anonymousPagination.page < anonymousPagination.totalPages,
                hasPrevPage: anonymousPagination.page > 1
              }}
              onPageChange={changePage}
            />
          </div>
        )
      )}

      {/* Modal de confirmation de suppression */}
      <DeleteAnonymousModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedForDelete({ ids: [], emails: [], deleteAll: false });
        }}
        onConfirm={handleDeleteConfirm}
        selectedForDelete={selectedForDelete}
        loading={deleting}
      />

      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  color: "#4B5563",
  marginBottom: 6,
  fontWeight: 500,
  letterSpacing: "0.3px"
};

const inputStyle: React.CSSProperties = {
  background: "white",
  border: "1px solid #D1D5DB",
  borderRadius: 10,
  padding: "8px 12px",
  fontSize: 14,
  outline: "none",
  minWidth: 130,
  transition: "border-color 0.2s, box-shadow 0.2s"
};

const secondaryButton: React.CSSProperties = {
  padding: "10px 18px",
  borderRadius: 10,
  border: "1px solid #D1D5DB",
  background: "white",
  color: "#374151",
  fontSize: 14,
  cursor: "pointer",
  transition: "all 0.2s",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 500
};

const tabStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: '12px 20px',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  transition: 'all 0.2s',
  borderRadius: '8px 8px 0 0'
};